import type { FieldValues } from 'react-hook-form-mui';
import { z } from 'zod/v4';
import type { CmsEditableField } from '@/services/cms/userDefinedForm/CmsEditableField';

export function createZodSchemaForFields<TFieldValues extends FieldValues = FieldValues>(fields: CmsEditableField[]) {
    type FieldKeys = Extract<keyof TFieldValues, string>;
    const schemaShape = {
        siteId: z.number().int().nullish(),
        locale: z.string().nullish(),
        formId: z.number().int().nullish(),
        formClassName: z.string().nullish(),
        date: z.number().nullish(),
    } as Record<FieldKeys, z.ZodTypeAny>;

    for (const field of fields) {
        const name = field.name as FieldKeys;

        const requiredMessage = field.customErrorMessage || `${field.title || name} is required`;
        if (field.className === 'EditableTextField') {
            schemaShape[name] = field.required ? z.string({ error: requiredMessage }).nonempty(requiredMessage) : z.string().nullish();
        } else if (field.className === 'EditableEmailField') {
            const message = field.customErrorMessage || `${field.title || name} must be a valid email address`;
            schemaShape[name] = field.required ? z.email(message) : z.union([z.literal('', { error: message }), z.email(message)]);
        } else if (field.className === 'EditableDateField') {
            const message = field.customErrorMessage || `${field.title || name} must be a valid date`;
            schemaShape[name] = field.required ? z.date({ error: message }).refine((date) => !isNaN(date.getTime()), message) : z.date({ error: message }).nullish();
        } else if (field.className === 'EditableNumericField') {
            schemaShape[name] = field.required ? z.number().nonnegative(`${field.title || name} must be a non-negative number`) : z.number().nullish();
        } else if (field.className === 'EditableCheckbox') {
            schemaShape[name] = field.required ? z.boolean().refine((val) => val, `${field.title || name} must be checked`) : z.boolean().nullish();
        } else if (field.className === 'EditableDropdown') {
            schemaShape[name] = field.required ? z.string().nonempty(`${field.title || name} is required`) : z.string().nullish();
        } else if (field.className === 'EditableRadioField') {
            schemaShape[name] = field.required ? z.string().nonempty(`${field.title || name} is required`) : z.string().nullish();
        } else if (field.className === 'EditableFileField') {
            schemaShape[name] = field.required ? z.instanceof(File, { error: `${field.title || name} must be a valid file` }) : z.instanceof(File).nullish();
        } else {
            schemaShape[name] = field.required ? z.string().nonempty(`${field.title || name} is required`) : z.string().nullish();
        }
    }
    return z.object(schemaShape) as z.ZodObject<Record<FieldKeys, z.ZodString>>;
}
